// Inline "Current Workflow" panel — displayed at the top of the assistant
// while a multi-step Workflow Draft Mode task is active.
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { friendlyError } from "@/lib/friendlyError";
import { ChevronDown, ChevronUp, Play, Save, X, Undo2, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { InstructorOnboardingWizard } from "./InstructorOnboardingWizard";
import { InstructorAssignmentWizard } from "./InstructorAssignmentWizard";
import { ClassCreationWizard } from "./ClassCreationWizard";

export interface WorkflowView {
  id: string;
  workflow_type: string;
  title: string;
  status: string;
  collected: Record<string, any>;
  missing: { key: string; label: string }[];
  validation: { key: string; level: "error" | "warn"; message: string }[];
  planned_actions: { index: number; kind: string; label: string; args: Record<string, unknown> }[];
  context: Record<string, any>;
  next_steps: string[];
  progress: number;
  waiting_on: string | null;
  updated_at: string;
}

interface Props {
  workflow: WorkflowView;
  functionName: string;
  onChange: (workflow: WorkflowView | null) => void;
  /** Increment to force the panel back open (e.g. new proposal arrived) */
  forceOpenNonce?: number;
  /** Optional: send a prefilled message to the assistant (used by wizards) */
  onSendMessage?: (text: string) => void;
}

const TYPE_LABEL: Record<string, string> = {
  instructor_onboarding: "Instructor Onboarding",
  class_creation: "Class Creation",
  instructor_assignment: "Instructor Assignment",
  popup_vendor: "Pop-Up Vendor",
  event_planning: "Event Planning",
  parent_onboarding: "Parent Onboarding",
  student_onboarding: "Student Onboarding",
  meeting_planning: "Meeting Planning",
};

export function CurrentWorkflowPanel({ workflow, functionName, onChange, forceOpenNonce = 0, onSendMessage }: Props) {
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState<null | "execute" | "save" | "cancel" | "review" | "undo">(null);
  const [executeResults, setExecuteResults] = useState<any[] | null>(null);

  useEffect(() => {
    if (forceOpenNonce > 0) setOpen(true);
  }, [forceOpenNonce]);

  // Instructor Onboarding gets a staff-friendly, step-by-step wizard instead of
  // the generic technical panel. Rendered after hooks to comply with rules of hooks.
  if (workflow.workflow_type === "instructor_onboarding") {
    return (
      <InstructorOnboardingWizard
        workflow={workflow}
        functionName={functionName}
        onChange={onChange}
        forceOpenNonce={forceOpenNonce}
        onSendMessage={onSendMessage}
      />
    );
  }
  if (workflow.workflow_type === "instructor_assignment") {
    return (
      <InstructorAssignmentWizard
        workflow={workflow}
        functionName={functionName}
        onChange={onChange}
        forceOpenNonce={forceOpenNonce}
      />
    );
  }
  if (workflow.workflow_type === "class_creation") {
    return (
      <ClassCreationWizard
        workflow={workflow}
        functionName={functionName}
        onChange={onChange}
        onSendMessage={onSendMessage}
        forceOpenNonce={forceOpenNonce}
      />
    );
  }
  const call = async (action: string, extra: Record<string, unknown> = {}) => {
    const { data, error } = await supabase.functions.invoke(functionName, {
      body: { action, draft_id: workflow.id, ...extra },
    });
    if (error) throw new Error(error.message);
    return data;
  };

  const doReview = async () => {
    setBusy("review");
    try {
      const r = await call("workflow_prepare_review");
      onChange(r?.workflow ?? null);
    } catch (e: unknown) { friendlyError({ what: "I couldn't get this workflow ready for review.", technical: e }); }
    finally { setBusy(null); }
  };
  const doExecute = async () => {
    setBusy("execute");
    try {
      const r = await call("workflow_execute");
      setExecuteResults(r?.results ?? []);
      onChange(r?.view ?? null);
      const failed = (r?.results ?? []).filter((x: any) => !x.ok);
      if (failed.length === 0) toast.success(`All done — ${r.results.length} step${r.results.length === 1 ? "" : "s"} finished.`);
      else friendlyError({ what: "The workflow stopped before finishing.", meaning: "Some steps were completed, others were not.", changed: "The completed steps have been saved.", nextStep: "Review the workflow, fix the flagged step, and try again.", technical: failed[0].error });
    } catch (e: unknown) { friendlyError({ what: "I couldn't run this workflow.", technical: e }); }
    finally { setBusy(null); }
  };
  const doSave = async () => {
    setBusy("save");
    try {
      const r = await call("workflow_save");
      onChange(r?.workflow ?? null);
      toast.success("Saved. Pick up where you left off any time.");
    } catch (e: unknown) { friendlyError({ what: "I couldn't save this workflow draft.", technical: e }); }
    finally { setBusy(null); }
  };
  const doCancel = async () => {
    if (!confirm("Discard this workflow draft?")) return;
    setBusy("cancel");
    try {
      await call("workflow_cancel");
      onChange(null);
    } catch (e: unknown) { friendlyError({ what: "I couldn't cancel this workflow draft.", technical: e }); }
    finally { setBusy(null); }
  };
  const doUndo = async () => {
    if (!confirm("Undo every action performed by this workflow? (only actions within the 5-minute undo window can be reverted)")) return;
    setBusy("undo");
    try {
      const r = await call("workflow_undo");
      onChange(r?.view ?? null);
      const failed = (r?.results ?? []).filter((x: any) => !x.ok);
      toast.success(`Undo complete — ${r.results.length - failed.length} reverted${failed.length ? `, ${failed.length} failed` : ""}.`);
    } catch (e: unknown) { friendlyError({ what: "I couldn't undo this workflow.", meaning: "Some actions may be past the 5-minute undo window.", technical: e }); }
    finally { setBusy(null); }
  };

  const errorCount = workflow.validation.filter((v) => v.level === "error").length;
  const readyToReview = workflow.missing.length === 0 && errorCount === 0 && workflow.status === "draft";
  const readyToExecute = workflow.status === "ready_for_review" && workflow.missing.length === 0 && errorCount === 0;
  const completed = workflow.status === "completed";

  return (
    <Card className="border-primary/30 bg-primary/5">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-primary/20">
        <Badge variant="outline" className="text-[10px] uppercase">{TYPE_LABEL[workflow.workflow_type] ?? workflow.workflow_type}</Badge>
        <div className="font-medium text-sm truncate flex-1">{workflow.title}</div>
        <span className="text-xs text-muted-foreground tabular-nums">{workflow.progress}%</span>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setOpen(!open)} aria-label={open ? "Collapse" : "Expand"}>
          {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </Button>
      </div>

      <div className="px-3 pt-2">
        <Progress value={workflow.progress} className="h-1.5" />
      </div>

      {open && (
        <div className="px-3 py-3 space-y-3 text-sm">
          {Object.keys(workflow.collected).length > 0 && (
            <div>
              <div className="text-xs font-semibold text-muted-foreground uppercase mb-1">Collected</div>
              <ul className="space-y-0.5">
                {Object.entries(workflow.collected)
                  .filter(([, v]) => v !== null && v !== undefined && v !== "")
                  .map(([k, v]) => (
                    <li key={k} className="text-xs flex gap-2">
                      <CheckCircle2 className="h-3 w-3 text-primary shrink-0 mt-0.5" />
                      <span className="text-muted-foreground min-w-[110px]">{prettyKey(k)}:</span>
                      <span className="font-medium truncate">{formatVal(v)}</span>
                    </li>
                  ))}
              </ul>
            </div>
          )}

          {workflow.missing.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-muted-foreground uppercase mb-1">Still needed</div>
              <ul className="space-y-0.5">
                {workflow.missing.map((m) => (
                  <li key={m.key} className="text-xs text-amber-700 dark:text-amber-400 flex gap-2">
                    <span>•</span> {m.label}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {workflow.validation.length > 0 && (
            <div className="space-y-1">
              {workflow.validation.map((v, i) => (
                <div key={i} className={`text-xs flex gap-1.5 ${v.level === "error" ? "text-destructive" : "text-amber-600"}`}>
                  <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" /> {v.message}
                </div>
              ))}
            </div>
          )}

          {workflow.planned_actions.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-muted-foreground uppercase mb-1">Actions to execute</div>
              <ol className="space-y-0.5 list-decimal list-inside">
                {workflow.planned_actions.map((a) => (
                  <li key={a.index} className="text-xs">{a.label}</li>
                ))}
              </ol>
            </div>
          )}

          {workflow.next_steps.length > 0 && workflow.status !== "completed" && (
            <div className="text-xs text-muted-foreground">
              <span className="font-semibold">Suggested next:</span> {workflow.next_steps.join(" · ")}
            </div>
          )}

          {executeResults && (
            <div className="rounded border p-2 space-y-1 bg-background">
              <div className="text-xs font-semibold">Execution report</div>
              {executeResults.map((r, i) => (
                <div key={i} className={`text-xs flex gap-1.5 ${r.ok ? "text-primary" : "text-destructive"}`}>
                  {r.ok ? <CheckCircle2 className="h-3 w-3 mt-0.5" /> : <AlertTriangle className="h-3 w-3 mt-0.5" />}
                  <span>{r.label}{r.error ? ` — ${r.error}` : ""}</span>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-1">
            {readyToReview && (
              <Button size="sm" onClick={doReview} disabled={!!busy}>
                {busy === "review" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                Ready for review
              </Button>
            )}
            {readyToExecute && (
              <Button size="sm" onClick={doExecute} disabled={!!busy}>
                {busy === "execute" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Play className="h-3.5 w-3.5 mr-1" />}
                Execute workflow
              </Button>
            )}
            {completed && (
              <Button size="sm" variant="outline" onClick={doUndo} disabled={!!busy}>
                {busy === "undo" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Undo2 className="h-3.5 w-3.5 mr-1" />}
                Undo workflow
              </Button>
            )}
            {!completed && (
              <>
                <Button size="sm" variant="outline" onClick={doSave} disabled={!!busy}>
                  {busy === "save" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                  Save draft
                </Button>
                <Button size="sm" variant="ghost" onClick={doCancel} disabled={!!busy}>
                  <X className="h-3.5 w-3.5 mr-1" /> Discard
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

function prettyKey(k: string) {
  return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
function formatVal(v: any) {
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (v === null || v === undefined) return "—";
  return String(v);
}
